/**
 * Fetch wrapper for the Vuzlyk JSON API.
 *
 * Responsibilities:
 *  - attach the access token as `Authorization: Bearer …` on every call
 *  - on a 401 response, attempt one silent refresh via /auth/refresh, then
 *    retry the original request with the new access token
 *  - normalize error responses: backend returns
 *    `{ "detail": "...", "code": "..." }`; we surface that as an `ApiError`
 *  - keep `URLSearchParams`-able query params out of the call site
 *
 * The token store is plug-in: pass any object that satisfies the
 * `TokenStore` protocol. Production wires this to AuthProvider's localStorage
 * store; tests substitute an in-memory fake.
 */

export interface TokenStore {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  setTokens(tokens: { accessToken: string; refreshToken: string }): void;
  clear(): void;
}

export interface ApiClientOptions {
  /** Base URL prefix; empty string for same-origin (browser default). */
  baseUrl?: string;
  /** Override the global fetch (used by tests). */
  fetchImpl?: typeof fetch;
  /** Token store; if omitted, requests go out unauthenticated. */
  tokens?: TokenStore;
  /** Hook for callers (AuthProvider) to learn the session was lost. */
  onAuthFailure?: () => void;
}

export interface ApiErrorBody {
  detail?: string;
  code?: string;
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly code: string | undefined;

  constructor(status: number, body: ApiErrorBody | string) {
    const detail = typeof body === "string" ? body : (body.detail ?? "request failed");
    super(detail);
    this.name = "ApiError";
    this.status = status;
    this.code = typeof body === "string" ? undefined : body.code;
  }
}

export interface RequestInit2 extends Omit<RequestInit, "body"> {
  /** JSON-serialisable value; sets ``Content-Type: application/json``. */
  json?: unknown;
  /** Raw body — typically ``FormData`` for multipart uploads. The
   *  browser sets the multipart boundary itself, so we deliberately
   *  *do not* set ``Content-Type`` when this is used. Mutually
   *  exclusive with ``json``. */
  body?: BodyInit;
  query?: Record<string, string | number | undefined>;
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly tokens: TokenStore | undefined;
  private readonly onAuthFailure: (() => void) | undefined;
  private inFlightRefresh: Promise<boolean> | null = null;

  constructor(opts: ApiClientOptions = {}) {
    // Every API call goes through the ``/api`` prefix so the SPA can
    // own the root paths (``/notes``, ``/people``, etc.) without
    // colliding with backend route handlers. Tests can override with
    // ``baseUrl: ""`` when they want raw paths.
    this.baseUrl = opts.baseUrl ?? "/api";
    this.fetchImpl = opts.fetchImpl ?? fetch.bind(globalThis);
    this.tokens = opts.tokens;
    this.onAuthFailure = opts.onAuthFailure;
  }

  async request<T>(path: string, init: RequestInit2 = {}): Promise<T> {
    const response = await this.send(path, init);
    if (response.status === 401 && this.tokens && this.tokens.getRefreshToken()) {
      const refreshed = await this.refreshOnce();
      if (refreshed) {
        const retry = await this.send(path, init);
        return await this.unwrap<T>(retry);
      }
      // refresh failed — surface the original 401 as a logout signal
      this.onAuthFailure?.();
    }
    return await this.unwrap<T>(response);
  }

  private async send(path: string, init: RequestInit2): Promise<Response> {
    const url = this.buildUrl(path, init.query);
    const headers = new Headers(init.headers);
    if (init.json !== undefined) {
      headers.set("Content-Type", "application/json");
    }
    const access = this.tokens?.getAccessToken();
    if (access) {
      headers.set("Authorization", `Bearer ${access}`);
    }
    const requestInit: RequestInit = { ...init, headers };
    if (init.json !== undefined) {
      requestInit.body = JSON.stringify(init.json);
    } else if (init.body !== undefined) {
      requestInit.body = init.body;
    }
    return await this.fetchImpl(url, requestInit);
  }

  private buildUrl(path: string, query: Record<string, string | number | undefined> | undefined): string {
    const url = `${this.baseUrl}${path}`;
    if (!query) return url;
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) {
        params.set(key, String(value));
      }
    }
    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  }

  private async unwrap<T>(response: Response): Promise<T> {
    if (response.status === 204) {
      return undefined as T;
    }
    const text = await response.text();
    const parsed: unknown = text ? safeJson(text) : null;
    if (!response.ok) {
      throw new ApiError(response.status, isErrorBody(parsed) ? parsed : (text || "request failed"));
    }
    return parsed as T;
  }

  /** Single-flight refresh: concurrent 401s share one /auth/refresh roundtrip. */
  private refreshOnce(): Promise<boolean> {
    if (this.inFlightRefresh !== null) {
      return this.inFlightRefresh;
    }
    const tokens = this.tokens;
    if (!tokens) {
      return Promise.resolve(false);
    }
    const refreshToken = tokens.getRefreshToken();
    if (!refreshToken) {
      return Promise.resolve(false);
    }

    const refresh = (async (): Promise<boolean> => {
      try {
        const response = await this.fetchImpl(`${this.baseUrl}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        if (!response.ok) {
          tokens.clear();
          return false;
        }
        const body = (await response.json()) as { tokens: { access_token: string; refresh_token: string } };
        tokens.setTokens({
          accessToken: body.tokens.access_token,
          refreshToken: body.tokens.refresh_token,
        });
        return true;
      } catch {
        tokens.clear();
        return false;
      } finally {
        this.inFlightRefresh = null;
      }
    })();

    this.inFlightRefresh = refresh;
    return refresh;
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function isErrorBody(value: unknown): value is ApiErrorBody {
  return typeof value === "object" && value !== null && ("detail" in value || "code" in value);
}
